import { normalizeShape, rebaseShapes, snapshotValueEqual, type Shape } from '@vd/annotation-core';
import type { ApiError } from '../../lib/apiClient';

interface AnnotationSaveResponse {
  snapshot?: unknown;
  version?: number;
}

export interface SaveAnnotationSnapshotOptions {
  snapshot: Shape[];
  base: Shape[];
  expectedVersion: number;
  signal: AbortSignal;
  save: (
    snapshot: Shape[],
    expectedVersion: number,
    signal: AbortSignal,
  ) => Promise<AnnotationSaveResponse>;
  /**
   * When the server's current shapes, at a version, are this person's own
   * earlier save, what that save was made from (`ownSaveBasis`). The snapshot
   * being saved descends from it, so it is rebased against that rather than
   * the older base: against the base, a shape undone since reads as the
   * server's addition and is restored.
   */
  ownBasis?: (snapshot: Shape[], version: number) => Shape[] | null;
}

export interface SavedAnnotationSnapshot {
  sent: Shape[];
  confirmed: Shape[];
  version: number;
}

/** Save with optimistic versioning, rebasing at most three conflicts. */
export async function saveAnnotationSnapshot(
  options: SaveAnnotationSnapshotOptions,
): Promise<SavedAnnotationSnapshot> {
  let sent = options.snapshot;
  let base = options.base;
  let expectedVersion = options.expectedVersion;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const response = await options.save(sent, expectedVersion, options.signal);
      const stored = Array.isArray(response.snapshot)
        ? (response.snapshot as Shape[]).map((shape) => normalizeShape(shape))
        : sent;
      if (!Number.isSafeInteger(response.version) || response.version === undefined) {
        const error = new Error('Annotation save response did not include a valid version.') as ApiError;
        error.code = 'annotation_version_missing';
        throw error;
      }
      return {
        sent,
        confirmed: snapshotValueEqual(stored, sent) ? sent : stored,
        version: response.version,
      };
    } catch (error) {
      const conflict = error as ApiError;
      if (
        conflict.status !== 409 ||
        conflict.code !== 'annotation_conflict' ||
        !Number.isSafeInteger(conflict.version) ||
        conflict.version === undefined ||
        !Array.isArray(conflict.snapshot)
      )
        throw error;
      const remote = (conflict.snapshot as Shape[]).map((shape) => normalizeShape(shape));
      sent = rebaseShapes(remote, sent, options.ownBasis?.(remote, conflict.version) ?? base);
      base = remote;
      expectedVersion = conflict.version;
      if (snapshotValueEqual(sent, remote)) {
        return { sent, confirmed: remote, version: conflict.version };
      }
    }
  }

  const error = new Error('Annotation changed repeatedly while saving.') as ApiError;
  error.status = 409;
  error.code = 'annotation_conflict';
  throw error;
}
